import { useEffect, useMemo, useState, type JSX, type ReactNode } from 'react'
import {
  ApiError,
  api,
  can,
  loadTiers,
  refusalReason,
  tierTableLoaded,
  type Issue,
  type TierRow,
  type Vendor,
} from '../lib/api'
import { Dialog, Status, Toggletip } from './kit'
import { RefusalNote } from './RefusalNote'
import { DateRuleNote, DateWarnNote } from './DateRuleNote'
import {
  checkContractDates,
  checkRelationshipStart,
  todayIndia,
  useLeftFields,
  warnContractEnded,
  warnContractStartFar,
  warnShortContract,
} from '../lib/dateRules'
import { ReasonTip } from './vendor/ReasonTip'
import '../styles/sweep.css'

// The section 7 master profile, asked once and used twice.
//
// # WHY ONE COMPONENT AND NOT A FORM ON EACH SCREEN
//
// The same twenty-seven questions are asked when a vendor is raised and when it
// is corrected a year later. Written twice they would drift within a quarter:
// one screen would gain the vendor type and the other would not, and the person
// who filled the form in on Monday would find a different form on Friday. So
// the groups live here and both screens render them in the same order, with
// only the identity and address questions differing, because those are asked
// with the India-specific checks the onboarding screen exists for.
//
// # THE GROUPS ARE THE REQUIREMENT'S GROUPS
//
// Basic information, location, services, then the risk profile last. Section 7
// lists them in that order and a reader who has the requirement open should be
// able to follow it down the screen. The risk profile is last because it is the
// only group whose answers decide something, and putting the tier inputs at the
// end lets the note that says so sit where it will be read.

const VENDOR_TYPES: { value: string; label: string }[] = [
  { value: '', label: 'Not stated' },
  { value: 'SERVICE_PROVIDER', label: 'Service provider' },
  { value: 'PRODUCT_SUPPLIER', label: 'Product supplier' },
  { value: 'SOFTWARE', label: 'Software' },
  { value: 'CLOUD_SERVICE', label: 'Cloud service' },
  { value: 'PROFESSIONAL_SERVICES', label: 'Professional services' },
  { value: 'STAFFING', label: 'Staffing' },
  { value: 'LOGISTICS', label: 'Logistics' },
  { value: 'FACILITIES', label: 'Facilities' },
  { value: 'FINANCIAL_SERVICES', label: 'Financial services' },
  { value: 'GOVERNMENT_BODY', label: 'Government body' },
  { value: 'OTHER', label: 'Other' },
]

// The same four words as the risk band, deliberately: a reader who has learned
// one scale in this product should not have to learn a second one meaning the
// same thing.
const CRITICALITY = [
  { value: '', label: 'Not stated' },
  { value: 'CRITICAL', label: 'Critical' },
  { value: 'HIGH', label: 'High' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LOW', label: 'Low' },
]

const HIGH_MEDIUM_LOW = [
  { value: '', label: 'Not stated' },
  { value: 'HIGH', label: 'High' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LOW', label: 'Low' },
]

const DATA_CATEGORIES = [
  { value: 'PERSONAL', label: 'Personal data' },
  { value: 'SENSITIVE_PERSONAL', label: 'Sensitive personal data' },
  { value: 'FINANCIAL', label: 'Financial data' },
  { value: 'HEALTH', label: 'Health data' },
]

const VOLUME_BANDS = [
  { value: '', label: 'Not stated' },
  { value: 'NONE', label: 'No records held' },
  { value: 'UNDER_10K', label: 'Under 10,000 records' },
  { value: 'UNDER_1M', label: 'Under 1 million records' },
  { value: 'OVER_1M', label: 'Over 1 million records' },
]

const SYSTEM_ACCESS = [
  { value: '', label: 'Not stated' },
  { value: 'NONE', label: 'No access to our systems' },
  { value: 'READ', label: 'Read access' },
  { value: 'WRITE', label: 'Write access' },
  { value: 'PRIVILEGED', label: 'Privileged or administrative access' },
]

// The keys are the JSON names the API accepts, so a field name reaches the edit
// route, the refusal that names it and the id of the control it belongs to
// without being translated on the way.
export type ProfileValues = {
  vendorType: string
  vendorCategory: string
  businessFunction: string
  ownerUserId: string
  businessOwnerUserId: string
  procurementOwnerUserId: string
  tprmOwnerUserId: string
  relationshipStartDate: string
  contractStartDate: string
  contractEndDate: string
  processingLocation: string
  dataCentreLocation: string
  serviceDescription: string
  businessProcess: string
  serviceCriticality: string
  dependencyLevel: string
  dataCategories: string[]
  volumeBand: string
  systemAccess: string
  criticalBusinessService: boolean
  productionAccess: boolean
  customerFacing: boolean
  continuityDependency: string
  regulatoryImpact: string
  regulatedProcess: boolean
  materialOutsourcing: boolean
  crossBorder: boolean
  subcontractorsDeclared: boolean
}

// What the identity half of the record adds when the profile is being edited
// rather than raised: the five facts the record read returns and the edit route
// validates. The address, the state, the MSME category and the Udyam number are
// deliberately absent. GetVendor returns two of the address's five columns, so
// a form offering the address would blank what it could not show, and an MSME
// category saved without its Udyam number is a section 43B(h) exposure that the
// edit route will not catch, because it reports a missing Udyam number only to
// a request that named that field.
export type RecordValues = ProfileValues & {
  legalName: string
  displayName: string
  constitution: string
  residency: string
  pan: string
}

export const EMPTY_PROFILE: ProfileValues = {
  vendorType: '',
  vendorCategory: '',
  businessFunction: '',
  ownerUserId: '',
  businessOwnerUserId: '',
  procurementOwnerUserId: '',
  tprmOwnerUserId: '',
  relationshipStartDate: '',
  contractStartDate: '',
  contractEndDate: '',
  processingLocation: '',
  dataCentreLocation: '',
  serviceDescription: '',
  businessProcess: '',
  serviceCriticality: '',
  dependencyLevel: '',
  dataCategories: [],
  volumeBand: '',
  systemAccess: '',
  criticalBusinessService: false,
  productionAccess: false,
  customerFacing: false,
  continuityDependency: '',
  regulatoryImpact: '',
  regulatedProcess: false,
  materialOutsourcing: false,
  crossBorder: false,
  subcontractorsDeclared: false,
}

// A date column is read back as YYYY-MM-DD. The slice is for the day something
// reads it as a timestamp instead: a date control handed 2026-04-01T00:00:00Z
// shows nothing at all and says nothing about why.
function dateValue(v: string | undefined): string {
  return v ? v.slice(0, 10) : ''
}

function recordValuesFrom(v: Vendor): RecordValues {
  return {
    vendorType: v.vendorType ?? '',
    vendorCategory: v.vendorCategory ?? '',
    businessFunction: v.businessFunction ?? '',
    ownerUserId: v.ownerUserId ?? '',
    businessOwnerUserId: v.businessOwnerUserId ?? '',
    procurementOwnerUserId: v.procurementOwnerUserId ?? '',
    tprmOwnerUserId: v.tprmOwnerUserId ?? '',
    relationshipStartDate: dateValue(v.relationshipStartDate),
    contractStartDate: dateValue(v.contractStartDate),
    contractEndDate: dateValue(v.contractEndDate),
    processingLocation: v.processingLocation ?? '',
    dataCentreLocation: v.dataCentreLocation ?? '',
    serviceDescription: v.serviceDescription ?? '',
    businessProcess: v.businessProcess ?? '',
    serviceCriticality: v.serviceCriticality ?? '',
    dependencyLevel: v.dependencyLevel ?? '',
    dataCategories: [...v.dataCategories],
    volumeBand: v.volumeBand ?? '',
    systemAccess: v.systemAccess ?? '',
    criticalBusinessService: v.criticalBusinessService ?? false,
    productionAccess: v.productionAccess ?? false,
    customerFacing: v.customerFacing ?? false,
    continuityDependency: v.continuityDependency ?? '',
    regulatoryImpact: v.regulatoryImpact ?? '',
    regulatedProcess: v.regulatedProcess,
    materialOutsourcing: v.materialOutsourcing,
    crossBorder: v.crossBorder,
    subcontractorsDeclared: v.subcontractorsDeclared,
    legalName: v.legalName,
    displayName: v.displayName,
    constitution: v.constitution,
    residency: v.residency,
    pan: v.pan ?? '',
  }
}

// The profile half of a create body. Trimmed here rather than at each control,
// because a trailing space in a business function is the kind of thing that
// makes the same function look like two of them in a report.
export function profileBody(f: ProfileValues): Record<string, unknown> {
  return {
    vendorType: f.vendorType,
    vendorCategory: f.vendorCategory.trim(),
    businessFunction: f.businessFunction.trim(),
    ownerUserId: f.ownerUserId.trim(),
    businessOwnerUserId: f.businessOwnerUserId.trim(),
    procurementOwnerUserId: f.procurementOwnerUserId.trim(),
    tprmOwnerUserId: f.tprmOwnerUserId.trim(),
    relationshipStartDate: f.relationshipStartDate,
    contractStartDate: f.contractStartDate,
    contractEndDate: f.contractEndDate,
    processingLocation: f.processingLocation.trim(),
    dataCentreLocation: f.dataCentreLocation.trim(),
    serviceDescription: f.serviceDescription.trim(),
    businessProcess: f.businessProcess.trim(),
    serviceCriticality: f.serviceCriticality,
    dependencyLevel: f.dependencyLevel,
    dataCategories: f.dataCategories,
    volumeBand: f.volumeBand,
    systemAccess: f.systemAccess,
    criticalBusinessService: f.criticalBusinessService,
    productionAccess: f.productionAccess,
    customerFacing: f.customerFacing,
    continuityDependency: f.continuityDependency,
    regulatoryImpact: f.regulatoryImpact,
    regulatedProcess: f.regulatedProcess,
    materialOutsourcing: f.materialOutsourcing,
    crossBorder: f.crossBorder,
    subcontractorsDeclared: f.subcontractorsDeclared,
  }
}

// The derivation migration 0027 puts in a trigger: HIGH and MEDIUM regulatory
// impact set regulated_process, LOW does not. The form shows what will be
// stored rather than what was typed, because the alternative is a tick that
// silently turns itself off on save.
function regulatedFrom(f: ProfileValues): boolean {
  if (f.regulatoryImpact === '') return f.regulatedProcess
  return f.regulatoryImpact !== 'LOW'
}

type StringKey = { [K in keyof RecordValues]: RecordValues[K] extends string ? K : never }[keyof RecordValues]
type BoolKey = { [K in keyof RecordValues]: RecordValues[K] extends boolean ? K : never }[keyof RecordValues]

// What every control needs and none of them should be handed one prop at a
// time: the values, the writer, the id prefix that keeps two forms on one page
// apart, and the two things that can be said about a single field, which is
// that it may not be changed and that what is in it is wrong.
type Ctx = {
  values: RecordValues
  onChange: <K extends keyof RecordValues>(key: K, value: RecordValues[K]) => void
  idPrefix: string
  locked: Record<string, string>
  issues: (field: string) => Issue[]
  // A date rule's message for the field (release 3.3, lib/dateRules.ts),
  // once the person has left it; and what to call when they leave a date.
  rule?: (field: string) => string
  // A date warning (release 3.3, M4): amber, never blocking.
  warn?: (field: string) => string
  leave?: (field: string) => void
}

type FieldShell = {
  ctx: Ctx
  name: string
  label: string
  // One line under the field, at most 80 characters.
  hint?: string
  // The reason behind the hint, when it is worth keeping: it opens from the (i)
  // beside the hint so the hint itself stays one line.
  tip?: string
  width?: 'wide' | 'full'
}

// The describedby of one field: whichever of the reason, the hint and the
// issues is actually on the screen. A control pointing at an id that is not
// rendered is read out as nothing at all.
function describedBy(s: FieldShell, id: string): string | undefined {
  const parts: string[] = []
  if (s.ctx.locked[s.name]) parts.push(id + '-lock')
  else if (s.hint) parts.push(id + '-hint')
  if (s.ctx.issues(s.name).length > 0) parts.push(id + '-issue')
  else if (s.ctx.rule?.(s.name)) parts.push(id + '-rule')
  return parts.length > 0 ? parts.join(' ') : undefined
}

// A lock the API wrote is its refusal, which the error at the top of the form
// already says in full and which would otherwise repeat under every field it
// refused. So each field says it is read only and keeps the API's sentence word
// for word behind its (i).
function ApiLock({ id, text }: { id: string; text: string }): JSX.Element {
  return (
    <p className="field-locked sw-row" id={id}>
      <Status tone="neutral">Read only</Status>
      <ReasonTip label="Why this field is read only" text={text} />
    </p>
  )
}

function Notes({ s, id }: { s: FieldShell; id: string }): JSX.Element {
  const lock = s.ctx.locked[s.name]
  const found = s.ctx.issues(s.name)
  return (
    <>
      {lock ? (
        <ApiLock id={id + '-lock'} text={lock} />
      ) : (
        s.hint && (
          <p className="hint" id={id + '-hint'}>
            {s.hint}
            {s.tip && (
              <>
                {' '}
                <Toggletip label={'About ' + (/^[A-Z]{2}/.test(s.label) ? s.label : s.label.toLowerCase())}>
                  {s.tip}
                </Toggletip>
              </>
            )}
          </p>
        )
      )}
      {found.length > 0 && (
        <div className="field-issue" id={id + '-issue'}>
          {found.map((i) => (
            <p key={i.code + i.detail} className="field-issue-line">
              <span className="field-issue-code">{i.code.replace(/_/g, ' ').toLowerCase()}</span> {i.detail}
            </p>
          ))}
        </div>
      )}
      {found.length === 0 && <DateRuleNote id={id + '-rule'} message={s.ctx.rule?.(s.name) ?? ''} />}
      {found.length === 0 && !s.ctx.rule?.(s.name) && <DateWarnNote id={id + '-warn'} message={s.ctx.warn?.(s.name) ?? ''} />}
    </>
  )
}

function fieldClass(width?: 'wide' | 'full'): string {
  return width ? 'field ' + width : 'field'
}

function TextField(props: FieldShell & { name: StringKey; placeholder?: string }): JSX.Element {
  const { ctx, name, label, placeholder } = props
  const id = ctx.idPrefix + '-' + name
  const lock = ctx.locked[name]
  return (
    <div className={fieldClass(props.width)}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="text"
        value={ctx.values[name]}
        placeholder={placeholder}
        disabled={Boolean(lock)}
        aria-invalid={ctx.issues(name).length > 0 ? true : undefined}
        aria-describedby={describedBy(props, id)}
        onChange={(e) => ctx.onChange(name, e.target.value)}
      />
      <Notes s={props} id={id} />
    </div>
  )
}

function DateField(props: FieldShell & { name: StringKey }): JSX.Element {
  const { ctx, name, label } = props
  const id = ctx.idPrefix + '-' + name
  const lock = ctx.locked[name]
  return (
    <div className={fieldClass(props.width)}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="date"
        value={ctx.values[name]}
        disabled={Boolean(lock)}
        aria-invalid={ctx.issues(name).length > 0 || ctx.rule?.(name) ? true : undefined}
        aria-describedby={describedBy(props, id)}
        onChange={(e) => ctx.onChange(name, e.target.value)}
        onBlur={() => ctx.leave?.(name)}
      />
      <Notes s={props} id={id} />
    </div>
  )
}

function SelectField(
  props: FieldShell & { name: StringKey; options: { value: string; label: string }[] },
): JSX.Element {
  const { ctx, name, label, options } = props
  const id = ctx.idPrefix + '-' + name
  const lock = ctx.locked[name]
  return (
    <div className={fieldClass(props.width)}>
      <label htmlFor={id}>{label}</label>
      <select
        id={id}
        value={ctx.values[name]}
        disabled={Boolean(lock)}
        aria-invalid={ctx.issues(name).length > 0 ? true : undefined}
        aria-describedby={describedBy(props, id)}
        onChange={(e) => ctx.onChange(name, e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <Notes s={props} id={id} />
    </div>
  )
}

function TextAreaField(props: FieldShell & { name: StringKey; rows?: number }): JSX.Element {
  const { ctx, name, label } = props
  const id = ctx.idPrefix + '-' + name
  const lock = ctx.locked[name]
  return (
    <div className={fieldClass(props.width ?? 'full')}>
      <label htmlFor={id}>{label}</label>
      <textarea
        id={id}
        rows={props.rows ?? 3}
        value={ctx.values[name]}
        disabled={Boolean(lock)}
        aria-invalid={ctx.issues(name).length > 0 ? true : undefined}
        aria-describedby={describedBy(props, id)}
        onChange={(e) => ctx.onChange(name, e.target.value)}
      />
      <Notes s={props} id={id} />
    </div>
  )
}

// A checkbox carries its label to the right of the box, so the reason a locked
// one cannot be ticked goes underneath the pair rather than inside the label.
function CheckField({
  ctx,
  name,
  label,
  checked,
  lockedBy,
}: {
  ctx: Ctx
  name: BoolKey
  label: string
  checked?: boolean
  lockedBy?: string
}): JSX.Element {
  const id = ctx.idPrefix + '-' + name
  const lock = lockedBy ?? ctx.locked[name]
  // A lock this form derives is its own short line; one the API refused is
  // said the way every refused field says it.
  return (
    <div className="check-locked">
      <label htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          checked={checked ?? ctx.values[name]}
          disabled={Boolean(lock)}
          aria-describedby={lock ? id + '-lock' : undefined}
          onChange={(e) => ctx.onChange(name, e.target.checked)}
        />
        {label}
      </label>
      {lockedBy ? (
        <p className="field-locked" id={id + '-lock'}>
          {lockedBy}
        </p>
      ) : (
        lock && <ApiLock id={id + '-lock'} text={lock} />
      )}
    </div>
  )
}

export function VendorProfileFields({
  ctx,
  identityLead,
}: {
  ctx: Ctx
  identityLead?: ReactNode
}): JSX.Element {
  const [tiers, setTiers] = useState<TierRow[]>(() => tierTableLoaded() ?? [])

  useEffect(() => {
    let live = true
    // The table is served under config.read, which somebody raising a vendor
    // need not hold, so it is asked for only where it will be answered. A
    // failed load leaves the legend off and the form working: the labels have a
    // shipped fallback, and a form that would not render because a reference
    // list did not answer is the worse trade.
    if (!can('config.read')) return
    loadTiers()
      .then((rows) => {
        if (live) setTiers(rows)
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [])

  const v = ctx.values
  const regulated = regulatedFrom(v)

  return (
    <>
      <fieldset className="block">
        <legend>Basic information</legend>
        <div className="vform">
          {identityLead}
          <SelectField
            ctx={ctx}
            name="vendorType"
            label="Vendor type"
            options={VENDOR_TYPES}
            hint="A fixed list, so an import can tell a typo from a new category"
            tip="The migration template carries the vendor type as a column, and an import cannot tell a typo from a new category."
          />
          <TextField
            ctx={ctx}
            name="vendorCategory"
            label="Vendor category"
            placeholder="The spend category your organisation uses"
            hint="Your own taxonomy, no list ships because none survives a second customer"
          />
          <TextField
            ctx={ctx}
            name="businessFunction"
            label="Business function"
            placeholder="Which function this vendor serves"
          />
          <DateField
            ctx={ctx}
            name="relationshipStartDate"
            label="Relationship start date"
            hint="When the vendor started supplying, often before the current contract"
          />
          <DateField ctx={ctx} name="contractStartDate" label="Contract start date" />
          <DateField
            ctx={ctx}
            name="contractEndDate"
            label="Contract end date"
            hint="Lets the register answer whose contract ends within 90 days"
          />
        </div>

        <p className="label-block" style={{ marginTop: 12 }} id={ctx.idPrefix + '-owners'}>
          Who answers for this vendor
        </p>
        <div className="vform" role="group" aria-labelledby={ctx.idPrefix + '-owners'}>
          <TextField ctx={ctx} name="ownerUserId" label="Internal owner" />
          <TextField ctx={ctx} name="businessOwnerUserId" label="Business owner" />
          <TextField ctx={ctx} name="procurementOwnerUserId" label="Procurement owner" />
          <TextField ctx={ctx} name="tprmOwnerUserId" label="TPRM owner" />
        </div>
      </fieldset>

      <fieldset className="block">
        <legend>Location</legend>
        <div className="vform">
          <TextField
            ctx={ctx}
            name="processingLocation"
            label="Processing location"
            placeholder="Where the work is done"
            hint="Where their people handle our data, not always where they are registered"
            width="wide"
          />
          <TextField
            ctx={ctx}
            name="dataCentreLocation"
            label="Data centre location"
            placeholder="Where the data rests"
            hint="The region the data sits in, read beside the cross-border declaration below"
            width="wide"
          />
        </div>
      </fieldset>

      <fieldset className="block">
        <legend>Services</legend>
        <div className="vform">
          <TextAreaField
            ctx={ctx}
            name="serviceDescription"
            label="Service or product provided"
            hint="What we buy from this vendor, in the words the business would use"
          />
          <TextField
            ctx={ctx}
            name="businessProcess"
            label="Business process supported"
            placeholder="The process that stops if this vendor stops"
            width="wide"
          />
          <SelectField ctx={ctx} name="serviceCriticality" label="Criticality of service" options={CRITICALITY} />
          <SelectField
            ctx={ctx}
            name="dependencyLevel"
            label="Dependency on this vendor"
            options={HIGH_MEDIUM_LOW}
            hint="How replaceable they are, not how much we spend"
          />
        </div>
      </fieldset>

      <fieldset className="block">
        <legend>Risk profile</legend>
        <p className="sw-row" style={{ margin: '0 0 10px' }}>
          <strong>These answers decide the tier</strong>
          <Toggletip hover label="What the tier chooses">
            Settled from this group alone, before any questionnaire is sent. The tier chooses the questionnaire, the
            reassessment cadence and how hard the answers that come back are judged.
          </Toggletip>
        </p>

        <div className="vform">
          <div className="field full">
            <span className="label-block" id={ctx.idPrefix + '-data-label'}>
              Data categories the vendor handles
            </span>
            <div
              className="checkrow"
              role="group"
              aria-labelledby={ctx.idPrefix + '-data-label'}
              aria-describedby={ctx.locked.dataCategories ? ctx.idPrefix + '-dataCategories-lock' : undefined}
            >
              {DATA_CATEGORIES.map((c) => {
                const id = ctx.idPrefix + '-dc-' + c.value
                return (
                  <label key={c.value} htmlFor={id}>
                    <input
                      id={id}
                      type="checkbox"
                      checked={v.dataCategories.includes(c.value)}
                      disabled={Boolean(ctx.locked.dataCategories)}
                      onChange={(e) =>
                        ctx.onChange(
                          'dataCategories',
                          e.target.checked
                            ? [...v.dataCategories, c.value]
                            : v.dataCategories.filter((x) => x !== c.value),
                        )
                      }
                    />
                    {c.label}
                  </label>
                )
              })}
            </div>
            {ctx.locked.dataCategories && (
              <ApiLock id={ctx.idPrefix + '-dataCategories-lock'} text={ctx.locked.dataCategories} />
            )}
          </div>

          <SelectField ctx={ctx} name="volumeBand" label="Record volume" options={VOLUME_BANDS} />
          <SelectField ctx={ctx} name="systemAccess" label="System access" options={SYSTEM_ACCESS} />
          <SelectField
            ctx={ctx}
            name="continuityDependency"
            label="Business continuity dependency"
            options={HIGH_MEDIUM_LOW}
            hint="How badly a failure here interrupts us, on the scale section 8 names"
          />
          <SelectField
            ctx={ctx}
            name="regulatoryImpact"
            label="Regulatory impact"
            options={HIGH_MEDIUM_LOW}
            hint="High means a failure here is reportable to a regulator, low means it is not"
            width="wide"
          />

          <div className="field full">
            <span className="label-block" id={ctx.idPrefix + '-flags-label'}>
              Declarations
            </span>
            <div className="checkcol" role="group" aria-labelledby={ctx.idPrefix + '-flags-label'}>
              <CheckField ctx={ctx} name="criticalBusinessService" label="Supports a critical business service" />
              <CheckField ctx={ctx} name="productionAccess" label="Has access to production" />
              <CheckField ctx={ctx} name="customerFacing" label="Customer-facing service" />
              <CheckField
                ctx={ctx}
                name="regulatedProcess"
                label="Supports a regulated process"
                checked={regulated}
                lockedBy={
                  v.regulatoryImpact === ''
                    ? undefined
                    : 'Set by the regulatory impact above: high and medium are regulated, low is not'
                }
              />
              <CheckField ctx={ctx} name="materialOutsourcing" label="Material outsourcing" />
              <CheckField ctx={ctx} name="crossBorder" label="Data moves outside India" />
              <CheckField ctx={ctx} name="subcontractorsDeclared" label="Subcontractors declared" />
            </div>
          </div>
        </div>

        {/* The tiers by name, each with what puts a vendor there behind a
            hover. The review of 17 September asked for the reasoning to leave
            the form, and a reader deciding a checkbox needs the names. */}
        {tiers.length > 0 && (
          <div className="sw-row" style={{ marginTop: 12, flexWrap: 'wrap' }} role="group" aria-labelledby={ctx.idPrefix + '-tier-legend'}>
            <span className="label-block" id={ctx.idPrefix + '-tier-legend'}>
              What these answers can decide
            </span>
            {tiers.map((t) => (
              <span key={t.tier} className="sw-row" style={{ gap: 4 }}>
                <span className="chip">{t.label}</span>
                {t.why && (
                  <Toggletip hover label={'What puts a vendor in ' + t.label}>
                    {t.why}
                  </Toggletip>
                )}
              </span>
            ))}
          </div>
        )}
      </fieldset>
    </>
  )
}

// ------------------------------------------------------------ the edit form

// Which fields this edit is changing. The route takes the fields being changed
// and nothing else: it refuses an edit that names none, and it refuses the
// whole edit when one named field is not the caller's to write, so sending back
// the fields nobody touched is how a corrected address becomes a 403 about the
// risk profile.
function changedFields(before: RecordValues, after: RecordValues): string[] {
  const out: string[] = []
  for (const key of Object.keys(after) as (keyof RecordValues)[]) {
    const a = after[key]
    const b = before[key]
    const same = Array.isArray(a) && Array.isArray(b) ? sameSet(a, b) : a === b
    if (!same) out.push(key)
  }
  return out.sort()
}

function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const set = new Set(b)
  return a.every((x) => set.has(x))
}

function bodyFor(values: RecordValues, fields: string[]): Record<string, unknown> {
  const all: Record<string, unknown> = { ...profileBody(values), ...identityBody(values) }
  const body: Record<string, unknown> = {}
  for (const f of fields) body[f] = all[f]
  return body
}

function identityBody(f: RecordValues): Record<string, unknown> {
  return {
    legalName: f.legalName.trim(),
    displayName: f.displayName.trim(),
    constitution: f.constitution,
    residency: f.residency,
    pan: f.pan.trim().toUpperCase(),
  }
}

// What the save is worth saying about itself. The tier is the consequence a
// person would otherwise find out about days later, on a screen they were not
// looking at, so a save that moved it says so on the spot.
//
// Short, because the rule that decided the tier is on the tier panel beside
// this and the history keeps who changed what.
function outcomeOf(before: Vendor, after: Vendor): string {
  const was = before.tier?.label || ''
  const now = after.tier?.label || ''
  if (now && was && now !== was) return 'Saved. Tier moved from ' + was + ' to ' + now + '.'
  if (now && !was) return 'Saved. Classified ' + now + '.'
  return 'Saved.'
}

export function VendorProfileEditor({
  vendor,
  onSaved,
  onCancel,
}: {
  vendor: Vendor
  onSaved: (v: Vendor, outcome: string) => void
  onCancel: () => void
}): JSX.Element {
  const stored = useMemo(() => recordValuesFrom(vendor), [vendor])
  const [values, setValues] = useState<RecordValues>(stored)
  const [issues, setIssues] = useState<Issue[]>([])
  const [locked, setLocked] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<ApiError | null>(null)
  const [aboutLeftOff, setAboutLeftOff] = useState(false)

  const changed = useMemo(() => changedFields(stored, values), [stored, values])

  const byField = useMemo(() => {
    const m = new Map<string, Issue[]>()
    for (const i of issues) {
      const list = m.get(i.field) ?? []
      list.push(i)
      m.set(i.field, list)
    }
    return m
  }, [issues])

  // D1 and D5 of the date rules, on the field they are about, once the person
  // has left a date. Only for a date being changed: a record that already
  // holds a same-day contract can still be saved for its other fields.
  const left = useLeftFields()
  const dateRules: Record<string, string> = {
    contractEndDate:
      changed.includes('contractStartDate') || changed.includes('contractEndDate')
        ? checkContractDates(values.contractStartDate, values.contractEndDate)
        : '',
    relationshipStartDate: changed.includes('relationshipStartDate')
      ? checkRelationshipStart(values.relationshipStartDate, todayIndia())
      : '',
  }
  // D2 to D4 (release 3.3, M4), for contract dates being changed: said once
  // the person has left them, never blocking the save.
  const movingContract = changed.includes('contractStartDate') || changed.includes('contractEndDate')
  const dateWarns: Record<string, string> = movingContract
    ? {
        contractStartDate: warnContractStartFar(values.contractStartDate, todayIndia()),
        contractEndDate:
          warnShortContract(values.contractStartDate, values.contractEndDate) ||
          warnContractEnded(values.contractEndDate, todayIndia()),
      }
    : {}
  const ctx: Ctx = {
    values,
    onChange: (key, value) => setValues((f) => ({ ...f, [key]: value })),
    idPrefix: 've',
    locked,
    issues: (field) => byField.get(field) ?? [],
    rule: (field) => left.shown(field, dateRules[field] ?? ''),
    warn: (field) => left.shown(field === 'contractStartDate' ? 'contractEndDate' : field, dateWarns[field] ?? ''),
    leave: (field) => left.leave(field === 'contractStartDate' ? 'contractEndDate' : field),
  }

  function save() {
    if (Object.values(dateRules).some(Boolean)) {
      left.leave(...Object.keys(dateRules))
      return
    }
    setSaving(true)
    setError(null)
    setIssues([])
    api
      .updateVendor(vendor.id, bodyFor(values, changed))
      .then(() => api.vendor(vendor.id))
      .then((fresh) => onSaved(fresh, outcomeOf(vendor, fresh)))
      .catch((err) => {
        const ae = err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))
        if (ae.status === 422 && ae.issues.length > 0) setIssues(ae.issues)
        if (ae.status === 403 && ae.fields.length > 0) {
          // The refused fields go back to what is on the record and are locked
          // with the sentence the API refused them in. Leaving the typed value
          // in place would keep sending it and keep being refused, and clearing
          // it without saying so would look like the save had worked.
          const reasons: Record<string, string> = {}
          for (const f of ae.fields) reasons[f] = ae.message
          setLocked((l) => ({ ...l, ...reasons }))
          setValues((f) => {
            const next = { ...f }
            for (const name of ae.fields as (keyof RecordValues)[]) {
              if (name in stored) (next as Record<string, unknown>)[name] = stored[name]
            }
            return next
          })
        }
        setError(ae)
      })
      .finally(() => setSaving(false))
  }

  const identityLead = (
    <>
      <TextField ctx={ctx} name="legalName" label="Legal name" width="wide" />
      <TextField ctx={ctx} name="displayName" label="Display name" width="wide" />
      <TextField
        ctx={ctx}
        name="pan"
        label="PAN"
        hint="Checked against the legal form on save"
        tip="The GSTINs already on file are not re-checked against it here, so a corrected PAN means the GSTINs need looking at too."
      />
      <SelectField
        ctx={ctx}
        name="constitution"
        label="Legal form"
        options={[
          { value: 'PRIVATE_LIMITED', label: 'Private limited company' },
          { value: 'PUBLIC_LIMITED', label: 'Public limited company' },
          { value: 'LLP', label: 'Limited liability partnership' },
          { value: 'PARTNERSHIP', label: 'Partnership firm' },
          { value: 'PROPRIETORSHIP', label: 'Proprietorship' },
          { value: 'TRUST', label: 'Trust' },
          { value: 'SOCIETY', label: 'Society or association' },
          { value: 'HUF', label: 'Hindu undivided family' },
          { value: 'FOREIGN_ENTITY', label: 'Foreign entity' },
          { value: 'GOVERNMENT_BODY', label: 'Government body' },
        ]}
      />
      <SelectField
        ctx={ctx}
        name="residency"
        label="Residency"
        options={[
          { value: 'RESIDENT', label: 'Resident in India' },
          { value: 'NON_RESIDENT', label: 'Non-resident' },
        ]}
      />
    </>
  )

  return (
    <form
      className="profile-editor"
      onSubmit={(e) => {
        e.preventDefault()
        save()
      }}
      noValidate
    >
      {/* What the form leaves off, and why, is a paragraph a person reads once,
          so the note keeps the fact and the reasons open in a drawer. */}
      <div className="note" style={{ marginBottom: 'var(--gap)' }}>
        <strong>Only what you change is sent</strong>
        <p className="sw-row">
          <span>Vendor code, registered address, MSME and Udyam number are not on this form</span>
          <button type="button" className="linkish" onClick={() => setAboutLeftOff(true)}>
            Why they are not here
          </button>
        </p>
      </div>
      <Dialog shape="drawer" kind="record" open={aboutLeftOff} onClose={() => setAboutLeftOff(false)} title="What this form leaves off">
        <div className="sw-stack">
          <div>
            <h3>The vendor code</h3>
            <p>
              It is the key every import row was mapped against, so reissuing one is a migration rather than a
              correction.
            </p>
          </div>
          <div>
            <h3>The registered address, the MSME declaration and the Udyam number</h3>
            <p>
              The record read returns two of the address's five columns, and a category saved without its Udyam
              number is a tax exposure this form cannot check.
            </p>
          </div>
        </div>
      </Dialog>

      {/* An approval in flight (release 3.2) is an answer, not a failure: the
          API's sentence says what to do, and it is shown alone. A 409's
          generic remedy talks about a duplicate identifier, which would send
          the reader looking for the wrong thing. */}
      {error && error.status !== 422 && error.code === 'APPROVAL_IN_FLIGHT' && (
        <RefusalNote error={error} heading="This edit was not saved" what="This edit" verb="saved" />
      )}
      {error && error.status !== 422 && error.code !== 'APPROVAL_IN_FLIGHT' && (
        <div className="error-box" role="alert">
          <h3>
            This edit was not saved ({error.code})
          </h3>
          <p>{error.message}</p>
          <p className="muted">{error.remedy}</p>
        </div>
      )}

      <VendorProfileFields ctx={ctx} identityLead={identityLead} />

      <div className="exc-actions">
        <button
          type="submit"
          className="primary"
          disabled={saving || changed.length === 0 || !can('vendor.edit')}
          title={can('vendor.edit') ? undefined : refusalReason('vendor.edit')}
          data-action="tprm.vendor,vendor-create-full,vendor-record.a01"
        >
          {saving ? 'Saving' : 'Save changes'}
        </button>
        <button type="button" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <span className="hint" role="status">
          {changed.length === 0
            ? 'Nothing has changed yet'
            : changed.length + ' field' + (changed.length === 1 ? '' : 's') + ' will be sent: ' + changed.join(', ')}
        </span>
      </div>
    </form>
  )
}
